import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { crmSettingsApi } from "../../../api/crmSettings";
import styles from "../CRMSettingsPage.module.css";

function HelpHint({ title, text }) {
  return (
    <span className={styles.helpHintCompact} title={text} aria-label={title}>
      ?
    </span>
  );
}

function AlertBox({ type = "info", children }) {
  const stylesMap = {
    error: {
      border: "1px solid rgba(255,80,80,0.18)",
      background: "rgba(255,80,80,0.07)",
      color: "rgba(255,190,190,0.95)",
    },
    success: {
      border: "1px solid rgba(80,200,120,0.18)",
      background: "rgba(80,200,120,0.07)",
      color: "rgba(200,255,220,0.95)",
    },
    muted: {
      border: "1px solid rgba(255,255,255,0.08)",
      background: "rgba(255,255,255,0.03)",
      color: "rgba(255,255,255,0.82)",
    },
  };

  return (
    <div
      style={{
        marginBottom: 10,
        padding: "9px 11px",
        borderRadius: 10,
        fontSize: 12,
        lineHeight: 1.35,
        ...stylesMap[type],
      }}
    >
      {children}
    </div>
  );
}

function RoleBanner({ isSuperAdmin, isAdmin, config, t }) {
  const message = isSuperAdmin
    ? t("crm_settings_rate_limit_superadmin_info", {
        defaultValue:
          "You control all tenant limits and can decide whether admins may edit them.",
      })
    : isAdmin
    ? config?.allow_admins_manage_rate_limits
      ? t("crm_settings_rate_limit_admin_can_modify", {
          defaultValue: "You can edit rate limit settings for this tenant.",
        })
      : t("crm_settings_rate_limit_admin_cannot_modify", {
          defaultValue: "Rate limit settings are managed by superadmin only.",
        })
    : null;

  if (!message) return null;

  return <div className={styles.roleBannerCompact}>{message}</div>;
}

export default function RateLimitTab({ isAdmin, isSuperAdmin }) {
  const { t } = useTranslation();

  const FIELD_GROUPS = [
    {
      title: t("crm_settings_rate_limit_group_general", {
        defaultValue: "General",
      }),
      fields: [
        [
          "rate_limit_general",
          t("crm_settings_rate_limit_field_general_api", {
            defaultValue: "General API",
          }),
          "100/min",
        ],
        [
          "rate_limit_public",
          t("crm_settings_rate_limit_field_public_endpoints", {
            defaultValue: "Public Endpoints",
          }),
          "20/min",
        ],
      ],
    },
    {
      title: t("crm_settings_rate_limit_group_auth", {
        defaultValue: "Auth",
      }),
      fields: [
        [
          "rate_limit_auth_login",
          t("crm_settings_rate_limit_field_login", {
            defaultValue: "Login",
          }),
          "5/min",
        ],
        [
          "rate_limit_auth_register",
          t("crm_settings_rate_limit_field_register", {
            defaultValue: "Register",
          }),
          "5/min",
        ],
        [
          "rate_limit_auth_refresh",
          t("crm_settings_rate_limit_field_refresh_token", {
            defaultValue: "Refresh Token",
          }),
          "10/min",
        ],
        [
          "rate_limit_auth_password_reset",
          t("crm_settings_rate_limit_field_password_reset", {
            defaultValue: "Password Reset",
          }),
          "3/min",
        ],
      ],
    },
    {
      title: t("crm_settings_rate_limit_group_email", {
        defaultValue: "Email",
      }),
      fields: [
        [
          "rate_limit_email_send",
          t("crm_settings_rate_limit_field_send_email", {
            defaultValue: "Send Email",
          }),
          "50/hour",
        ],
        [
          "rate_limit_email_bulk",
          t("crm_settings_rate_limit_field_bulk_email", {
            defaultValue: "Bulk Email",
          }),
          "10/hour",
        ],
        [
          "rate_limit_email_fetch",
          t("crm_settings_rate_limit_field_fetch_email", {
            defaultValue: "Fetch Email",
          }),
          "60/hour",
        ],
      ],
    },
    {
      title: t("crm_settings_rate_limit_group_telephony", {
        defaultValue: "Telephony",
      }),
      fields: [
        [
          "rate_limit_telephony_call",
          t("crm_settings_rate_limit_field_calls", {
            defaultValue: "Calls",
          }),
          "30/min",
        ],
        [
          "rate_limit_telephony_token",
          t("crm_settings_rate_limit_field_telephony_token", {
            defaultValue: "Telephony Token",
          }),
          "20/min",
        ],
      ],
    },
    {
      title: t("crm_settings_rate_limit_group_chat", {
        defaultValue: "Chat",
      }),
      fields: [
        [
          "rate_limit_chat_message",
          t("crm_settings_rate_limit_field_chat_messages", {
            defaultValue: "Chat Messages",
          }),
          "60/min",
        ],
        [
          "rate_limit_chat_thread",
          t("crm_settings_rate_limit_field_chat_threads", {
            defaultValue: "Chat Threads",
          }),
          "20/min",
        ],
      ],
    },
    {
      title: t("crm_settings_rate_limit_group_reports", {
        defaultValue: "Reports",
      }),
      fields: [
        [
          "rate_limit_reports_export",
          t("crm_settings_rate_limit_field_export_reports", {
            defaultValue: "Export Reports",
          }),
          "5/hour",
        ],
        [
          "rate_limit_reports_generate",
          t("crm_settings_rate_limit_field_generate_reports", {
            defaultValue: "Generate Reports",
          }),
          "10/hour",
        ],
      ],
    },
    {
      title: t("crm_settings_rate_limit_group_ai", {
        defaultValue: "AI",
      }),
      fields: [
        [
          "rate_limit_ai_summary",
          t("crm_settings_rate_limit_field_ai_summary", {
            defaultValue: "AI Summary",
          }),
          "20/min",
        ],
        [
          "rate_limit_ai_score",
          t("crm_settings_rate_limit_field_ai_score", {
            defaultValue: "AI Score",
          }),
          "20/min",
        ],
      ],
    },
  ];

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [draft, setDraft] = useState({});
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [accessDenied, setAccessDenied] = useState(false);

  const canEditLimits = useMemo(() => {
    if (!config) return false;
    return isSuperAdmin || (isAdmin && !!config.allow_admins_manage_rate_limits);
  }, [config, isAdmin, isSuperAdmin]);

  const hasChanges = useMemo(() => Object.keys(draft).length > 0, [draft]);

  const clearMessages = () => {
    setError("");
    setSuccess("");
  };

  const loadConfig = async () => {
    try {
      setLoading(true);
      setAccessDenied(false);
      clearMessages();

      const data = await crmSettingsApi.getRateLimitsConfig();
      setConfig(data || null);
      setDraft({});
    } catch (err) {
      const status = err?.response?.status;
      const detail =
        err?.response?.data?.detail ||
        t("crm_settings_rate_limit_load_error", {
          defaultValue: "Failed to load tenant rate limit settings.",
        });

      if (status === 403) setAccessDenied(true);

      setConfig(null);
      setDraft({});
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  const setField = (field, value) => {
    clearMessages();
    setDraft((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    if (!hasChanges || !canEditLimits) return;

    try {
      setSaving(true);
      clearMessages();

      const payload = { ...draft };

      if (!isSuperAdmin) {
        delete payload.allow_admins_manage_rate_limits;
      }

      const data = await crmSettingsApi.updateRateLimitsConfig(payload);
      setConfig(data || null);
      setDraft({});

      setSuccess(
        t("crm_settings_rate_limit_saved", {
          defaultValue: "Rate limit settings saved.",
        })
      );
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          t("crm_settings_rate_limit_save_error", {
            defaultValue: "Failed to save rate limit settings.",
          })
      );
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEnabled = async (checked) => {
    if (!canEditLimits) return;

    try {
      setSaving(true);
      clearMessages();

      const data = await crmSettingsApi.toggleRateLimits(checked);

      setConfig((prev) =>
        prev
          ? {
              ...prev,
              rate_limiting_enabled: data?.rate_limiting_enabled,
            }
          : prev
      );

      setSuccess(
        checked
          ? t("crm_settings_rate_limit_enabled_success", {
              defaultValue: "Rate limiting enabled.",
            })
          : t("crm_settings_rate_limit_disabled_success", {
              defaultValue: "Rate limiting disabled.",
            })
      );
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          t("crm_settings_rate_limit_toggle_error", {
            defaultValue: "Failed to change rate limiting status.",
          })
      );
    } finally {
      setSaving(false);
    }
  };

  const handleToggleAdminAccess = async (checked) => {
    if (!isSuperAdmin) return;

    try {
      setSaving(true);
      clearMessages();

      const data = await crmSettingsApi.toggleAdminsManageRateLimits(checked);
      setConfig(data || null);

      setDraft((prev) => {
        const next = { ...prev };
        delete next.allow_admins_manage_rate_limits;
        return next;
      });

      setSuccess(
        checked
          ? t("crm_settings_rate_limit_admin_access_enabled", {
              defaultValue: "Admin access to rate limits enabled.",
            })
          : t("crm_settings_rate_limit_admin_access_disabled", {
              defaultValue: "Admin access to rate limits disabled.",
            })
      );
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          t("crm_settings_rate_limit_admin_access_error", {
            defaultValue: "Failed to update admin access.",
          })
      );
    } finally {
      setSaving(false);
    }
  };

  const handleResetDefaults = async () => {
    if (!canEditLimits) return;

    try {
      setSaving(true);
      clearMessages();

      const data = await crmSettingsApi.resetRateLimitsToDefault();
      setConfig(data || null);
      setDraft({});

      setSuccess(
        t("crm_settings_rate_limit_reset_success", {
          defaultValue: "Rate limits reset to defaults.",
        })
      );
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          t("crm_settings_rate_limit_reset_error", {
            defaultValue: "Failed to reset rate limits.",
          })
      );
    } finally {
      setSaving(false);
    }
  };

  const getValue = (field) => {
    if (draft[field] !== undefined) return draft[field];
    return config?.[field] ?? "";
  };

  if (loading) {
    return (
      <div className={styles.rateCompactShell}>
        <div className={styles.rateCompactLoading}>
          {t("loading", { defaultValue: "Loading" })}...
        </div>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className={styles.rateCompactShell}>
        <div className={styles.rateCompactSectionCard}>
          <div className={styles.rateCompactTopbar}>
            <div className={styles.rateCompactHeading}>
              <div className={styles.rateCompactTitleRow}>
                <h2 className={styles.rateCompactTitle}>
                  {t("crm_settings_tab_rate_limit", {
                    defaultValue: "Rate Limiting",
                  })}
                </h2>
                <HelpHint
                  title={t("crm_settings_rate_limit_title", {
                    defaultValue: "Rate Limiting",
                  })}
                  text={t("crm_settings_rate_limit_help", {
                    defaultValue:
                      "Configure tenant-specific API rate limits and admin access.",
                  })}
                />
              </div>
              <div className={styles.rateCompactSubtitle}>
                {t("crm_settings_rate_limit_subtitle", {
                  defaultValue: "Real tenant limits from backend configuration.",
                })}
              </div>
            </div>

            <div className={styles.rateCompactActions}>
              <button
                type="button"
                className={styles.secondaryActionCompact}
                onClick={loadConfig}
                disabled={saving}
              >
                {t("refresh", { defaultValue: "Refresh" })}
              </button>
            </div>
          </div>
        </div>

        <AlertBox type="error">
          <strong>
            {t("crm_settings_access_denied", {
              defaultValue: "Access denied",
            })}
          </strong>
          <div style={{ marginTop: 4 }}>{error}</div>
        </AlertBox>
      </div>
    );
  }

  return (
    <div className={styles.rateCompactShell}>
      {error ? <AlertBox type="error">{error}</AlertBox> : null}
      {success ? <AlertBox type="success">{success}</AlertBox> : null}

      <RoleBanner
        isSuperAdmin={isSuperAdmin}
        isAdmin={isAdmin}
        config={config}
        t={t}
      />

      <section className={styles.rateCompactSectionCard}>
        <div className={styles.rateCompactSectionHeader}>
          <div className={styles.rateCompactSectionHeaderMain}>
            <div className={styles.rateCompactSectionTitleRow}>
              <h2 className={styles.rateCompactSectionMainTitle}>
                {t("crm_settings_general_access_title", {
                  defaultValue: "General Access",
                })}
              </h2>
            </div>
            <div className={styles.rateCompactSectionSubtext}>
              {t("crm_settings_general_access_subtitle", {
                defaultValue:
                  "Global permissions and tenant-level control for rate limiting.",
              })}
            </div>
          </div>
          <span className={styles.compactPill}>
            {t("crm_settings_rate_limit_pill_tenant", {
              defaultValue: "Tenant",
            })}
          </span>
        </div>

        <div className={styles.rateCompactToggleGrid}>
          <div className={styles.rateCompactToggleItem}>
            <div className={styles.rateCompactToggleMeta}>
              <div className={styles.rateCompactToggleLabel}>
                {t("crm_settings_rate_limit_enabled", {
                  defaultValue: "Enable Rate Limiting",
                })}
              </div>
              <div className={styles.rateCompactToggleHint}>
                {t("crm_settings_rate_limit_enabled_help", {
                  defaultValue:
                    "Globally enable or disable rate limiting for this tenant.",
                })}
              </div>
            </div>

            <label className={styles.rateCompactToggleControl}>
              <input
                type="checkbox"
                checked={!!config?.rate_limiting_enabled}
                onChange={(e) => handleToggleEnabled(e.target.checked)}
                disabled={saving || !canEditLimits}
              />
              <span>
                {config?.rate_limiting_enabled
                  ? t("crm_settings_on", { defaultValue: "On" })
                  : t("crm_settings_off", { defaultValue: "Off" })}
              </span>
            </label>
          </div>

          {isSuperAdmin && (
            <div className={styles.rateCompactToggleItem}>
              <div className={styles.rateCompactToggleMeta}>
                <div className={styles.rateCompactToggleLabel}>
                  {t("crm_settings_allow_admin_modify", {
                    defaultValue: "Allow Admin to Modify",
                  })}
                </div>
                <div className={styles.rateCompactToggleHint}>
                  {t("crm_settings_allow_admin_modify_help", {
                    defaultValue:
                      "Permit tenant admins to view and change tenant rate limits.",
                  })}
                </div>
              </div>

              <label className={styles.rateCompactToggleControl}>
                <input
                  type="checkbox"
                  checked={!!config?.allow_admins_manage_rate_limits}
                  onChange={(e) => handleToggleAdminAccess(e.target.checked)}
                  disabled={saving}
                />
                <span>
                  {config?.allow_admins_manage_rate_limits
                    ? t("crm_settings_on", { defaultValue: "On" })
                    : t("crm_settings_off", { defaultValue: "Off" })}
                </span>
              </label>
            </div>
          )}
        </div>

        {!canEditLimits && (
          <div className={styles.rateCompactMutedNotice}>
            {t("crm_settings_rate_limit_admin_cannot_modify_notice", {
              defaultValue:
                "Only superadmin can modify rate limits. Ask your superadmin to enable admin modifications.",
            })}
          </div>
        )}
      </section>

      <section className={styles.rateCompactSectionCard}>
        <div className={styles.rateCompactTopbar}>
          <div className={styles.rateCompactHeading}>
            <div className={styles.rateCompactTitleRow}>
              <h2 className={styles.rateCompactTitle}>
                {t("crm_settings_rate_limit_rules_title", {
                  defaultValue: "Rate Limit Rules",
                })}
              </h2>
              <HelpHint
                title={t("crm_settings_rate_limit_title", {
                  defaultValue: "Rate Limiting",
                })}
                text={t("crm_settings_rate_limit_help", {
                  defaultValue:
                    "Configure tenant-specific API rate limits and admin access.",
                })}
              />
            </div>
            <div className={styles.rateCompactSubtitle}>
              {t("crm_settings_rate_limit_subtitle", {
                defaultValue: "Real tenant limits from backend configuration.",
              })}
            </div>
          </div>

          <div className={styles.rateCompactActions}>
            <button
              type="button"
              className={styles.secondaryActionCompact}
              onClick={handleResetDefaults}
              disabled={saving || !canEditLimits}
            >
              {t("crm_settings_reset_defaults", {
                defaultValue: "Defaults",
              })}
            </button>

            <button
              type="button"
              className={styles.secondaryActionCompact}
              onClick={loadConfig}
              disabled={saving}
            >
              {t("refresh", { defaultValue: "Refresh" })}
            </button>

            <button
              type="button"
              className={styles.primaryActionCompact}
              onClick={handleSave}
              disabled={saving || !hasChanges || !canEditLimits}
            >
              {saving
                ? t("saving", { defaultValue: "Saving..." })
                : t("save_changes", { defaultValue: "Save changes" })}
            </button>
          </div>
        </div>

        <details className={styles.rateCompactAccordion} open>
          <summary className={styles.rateCompactAccordionSummary}>
            <div className={styles.rateCompactAccordionSummaryMain}>
              <span className={styles.rateCompactAccordionTitle}>
                {t("crm_settings_rate_limit_rules_label", {
                  defaultValue: "Show rate limit rules",
                })}
              </span>
              <span className={styles.rateCompactAccordionHint}>
                {t("crm_settings_rate_limit_rules_hint", {
                  defaultValue:
                    "General, Auth, Email, Telephony, Chat, Reports and AI limits",
                })}
              </span>
            </div>
            <span className={styles.rateCompactAccordionChevron}><i class="lni lni-chevron-down"></i></span>
          </summary>

          <div className={styles.rateCompactAccordionBody}>
            <div className={styles.rateCompactFieldsViewport}>
              <div className={styles.rateCompactGrid}>
                {FIELD_GROUPS.map((group) => (
                  <section key={group.title} className={styles.rateCompactCard}>
                    <div className={styles.rateCompactCardHead}>
                      <div className={styles.rateCompactCardTitle}>
                        {group.title}
                      </div>
                    </div>

                    <div className={styles.rateCompactFieldList}>
                      {group.fields.map(([field, label, example]) => (
                        <div key={field} className={styles.rateCompactFieldRow}>
                          <div className={styles.rateCompactFieldMeta}>
                            <label className={styles.rateCompactFieldLabel}>
                              {label}
                            </label>
                            <span className={styles.rateCompactFieldHint}>
                              {example}
                            </span>
                          </div>

                          <input
                            className={styles.rateCompactInput}
                            type="text"
                            value={getValue(field)}
                            onChange={(e) => setField(field, e.target.value)}
                            disabled={saving || !canEditLimits}
                            placeholder={example}
                          />
                        </div>
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            </div>
          </div>
        </details>
      </section>
    </div>
  );
}